import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import PlanCard from './PlanCard';
import ProfileMenu from './ProfileMenu';
import { hasPermission, isHmView } from '../utils/permissions';

export function storedSession() {
  const read = (k) => { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (e) { return null; } };
  return { user: read('user') || {}, company: read('company') || {} };
}

const Icon = ({ d }) => (
  <svg className="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);
const ICONS = {
  dashboard: 'M4 5h6v6H4zM14 5h6v6h-6zM4 15h6v4H4zM14 15h6v4h-6z',
  jobs: 'M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2m-9 0h12a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2z',
  approvals: 'M9 12l2 2 4-4M6 3h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V5a2 2 0 012-2z',
  screen: 'M4 5h16v10H4zM8 19h8M12 15v4',
  myinterviews: 'M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1zm4 10l2 2 4-4',
  interviews: 'M12 14a3 3 0 003-3V6a3 3 0 10-6 0v5a3 3 0 003 3zm6-3a6 6 0 01-12 0m6 6v3',
  candidates: 'M16 11a4 4 0 10-8 0 4 4 0 008 0zM4 21v-1a6 6 0 016-6h4a6 6 0 016 6v1M19 8h4M21 6v4',
  organization: 'M17 20h5v-2a3 3 0 00-5.4-1.8M17 20H7m10 0v-2c0-.7-.1-1.3-.4-1.8M7 20H2v-2a3 3 0 015.4-1.8M7 20v-2c0-.7.1-1.3.4-1.8m0 0a5 5 0 019.2 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
};
const PLAN_RANK = { essential: 1, professional: 2, enterprise: 3 };

// The approvals waiting for the signed-in person (the sidebar badge), and whether they approve at all.
export function useApprovalCount() {
  const [c, setC] = useState(null);
  useEffect(() => {
    if (!localStorage.getItem('token')) return undefined;
    let live = true;
    const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    fetch(`${BASE}/api/approvals/count`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => (r && r.ok ? r.json() : null)).then((d) => { if (live && d) setC(d); }).catch(() => {});
    return () => { live = false; };
  }, []);
  return c;
}

// Part C: feedback due on your rounds (the My interviews badge) — only for people with the Interview permission
export function useMyInterviewsDue(enabled) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled || !localStorage.getItem('token')) return undefined;
    let live = true;
    const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    fetch(`${BASE}/api/my-interviews/summary`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => (r && r.ok ? r.json() : null)).then((d) => { if (live && d) setN(d.feedback_due || 0); }).catch(() => {});
    return () => { live = false; };
  }, [enabled]);
  return n;
}

// The sidebar of every signed-in screen (approved mockups): Dashboard; HIRING: Jobs · Approvals (people who approve,
// with a count) · Candidates (admins + Recruit, bundle 4a-2) · Technical Screen · Interviews; ADMIN: Organization; the plan card (company admins only).
// Technical Screen isn't offered in the hiring-manager view; Interviews needs Enterprise for a recruiter (as on the
// dashboards). onSelect(key) lets a dashboard switch its own panels instead of navigating.
export function Sidebar({ active, onSelect, footer }) {
  const navigate = useNavigate();
  const { user, company } = storedSession();
  const admin = user.role === 'company_super_admin';
  const hm = isHmView(user);
  const enterprise = (PLAN_RANK[(company.subscription_plan || '').toLowerCase()] || 1) >= 3;
  const home = admin ? '/dashboard/company-admin' : '/dashboard/user';
  const approvals = useApprovalCount();
  const interviewer = hasPermission(user, 'interview');
  const due = useMyInterviewsDue(interviewer);
  const targets = { dashboard: home, jobs: '/jobs', approvals: '/approvals', screen: '/technical-screen',
    interviews: `${home}?menu=my-interviews`, organization: '/organization', candidates: '/candidates', myinterviews: '/my-interview-rounds' };
  const item = (key, label, count) => (
    <button key={key} type="button" onClick={() => (onSelect && !['approvals', 'candidates', 'myinterviews'].includes(key) ? onSelect(key) : navigate(targets[key]))} aria-current={active === key ? 'page' : undefined}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
        active === key ? 'bg-[#E8F1FB] text-[#0066CC]' : 'text-[#334155] hover:bg-[#F5F7FB]'}`}>
      <Icon d={ICONS[key]} />{label}
      {count > 0 && <span className="ml-auto bg-[#DC2626] text-white text-[11px] font-bold rounded-full px-[7px] py-px" data-testid={key === 'approvals' ? 'approvals-count' : `${key}-count`}
        aria-label={key === 'myinterviews' ? `${count} feedback due` : `${count} waiting`}>{count}</span>}
    </button>
  );
  const section = (label) => <p className="px-3 pt-5 pb-1 text-[10px] font-bold tracking-widest text-[#94A3B8]">{label}</p>;
  return (
    <aside className="w-60 shrink-0 bg-white border-r border-[#E6E9F0] flex flex-col" aria-label="Main navigation" data-testid="sidebar">
      <div className="px-5 py-4"><img src="/Reqt5d-Logo.png" alt="Quali5d" className="h-12" /></div>
      <nav className="px-3 space-y-1 flex-1">
        {item('dashboard', 'Dashboard')}
        {section('HIRING')}
        {item('jobs', 'Jobs')}
        {(admin || hasPermission(user, 'recruit')) && item('candidates', 'Candidates')}
        {approvals && approvals.can_approve && item('approvals', 'Approvals', approvals.total)}
        {(admin || !hm) && item('screen', 'Technical Screen')}
        {(admin || hm || enterprise) && item('interviews', 'Interviews')}
        {interviewer && item('myinterviews', 'My interviews', due)}
        {admin && (<>{section('ADMIN')}{item('organization', 'Organization')}</>)}
      </nav>
      {footer !== undefined ? footer : (admin && <PlanCard company={company} />)}
    </aside>
  );
}

// The top bar of every signed-in screen: company name · bell · profile menu (onMenu: the menu button on phones).
export function TopBar({ onSubscription, onMenu }) {
  const { user, company } = storedSession();
  return (
    <header className="h-16 bg-white border-b border-[#E6E9F0] px-4 sm:px-6 flex items-center justify-between gap-2 sticky top-0 z-30">
      {onMenu && <button type="button" className="md:hidden -ml-1 w-10 h-10 rounded-lg text-xl text-[#334155] hover:bg-[#F5F7FB]" aria-label="Menu"
        onClick={onMenu} data-testid="menu-button">☰</button>}
      <p className="text-sm font-bold text-[#334155] truncate flex-1 min-w-0">{company.company_name || ''}</p>
      <div className="flex items-center gap-4">
        <NotificationBell />
        <ProfileMenu user={user} onSubscription={onSubscription} />
      </div>
    </header>
  );
}

// The shell of the Organization and Jobs screens. Under 768 px (5b) the sidebar opens from the ☰ button.
export default function AppShell({ active, children }) {
  const [menu, setMenu] = useState(false);
  return (
    <div className="min-h-screen flex bg-[#F5F7FB] font-inter text-[#0F172A]">
      <div className="hidden md:flex"><Sidebar active={active} /></div>
      {menu && <div className="md:hidden fixed inset-0 z-40 flex" data-testid="mobile-menu">
        <div className="flex max-w-[85vw] overflow-y-auto"><Sidebar active={active} /></div>
        <button type="button" className="flex-1 bg-black/30" aria-label="Close menu" onClick={() => setMenu(false)} /></div>}
      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar onMenu={() => setMenu(true)} />
        <main className="flex-1 min-w-0 px-4 py-5 sm:px-8 sm:py-7 max-w-[1440px] w-full">{children}</main>
      </div>
    </div>
  );
}
